import PropTypes from 'prop-types';
import cx from "ramda-extension/es/cx";
import React from 'react';
import FormGroup from '../../../molecules/FormGroup';
import { validationDisplayTypePropType } from '../../../molecules/Validation/propTypes';
import { useDefaultComponentValue } from '../../../utils/useDefaultComponentValue';
import AngleDown from '../../SvgIcon/Icons/AngleDown';
import AngleUp from '../../SvgIcon/Icons/AngleUp';
import TextField, { InputControl } from '../../TextField';
const COMPONENT_NAME = 'Spinner';
const isOutOfMinRange = (min, newValue)=>min !== null && min > newValue;
const isOutOfMaxRange = (max, newValue)=>max !== null && max < newValue;
const getEnforcedValue = ({ value, min, max })=>{
    if (isOutOfMaxRange(max, value)) {
        return max;
    } else if (isOutOfMinRange(min, value)) {
        return min;
    } else {
        return value;
    }
};
const Spinner = (inProps)=>{
    const props = useDefaultComponentValue({
        inProps,
        name: COMPONENT_NAME
    });
    const { disableDirectInput, onChange, min = null, max = null, step = 1, onBlur, value, controlsPanelStepUpIcon = /*#__PURE__*/ React.createElement(AngleUp, null), controlsPanelStepDownIcon = /*#__PURE__*/ React.createElement(AngleDown, null), disabled, id, labelText, labelNote, labelClassName, labelPosition, className, floatingLabelText, floatingLabelClassName, controlClassName, controlInputClassName, validationText, validationClassName, hideValidationText, displayValue, placeholder, onClick, onFocus, outline, tooltip, required, onKeyUp, validationDisplayType, sizeSmall, rawOutput, enforceRange, simple } = props;
    const handleOnBlur = (event)=>{
        if (enforceRange && !rawOutput) {
            const newValue = Math.floor(Number(event.target.value));
            const enforcedValue = getEnforcedValue({
                value: newValue,
                min,
                max
            });
            onChange?.(event, enforcedValue);
        }
        onBlur?.(event);
    };
    const changeValue = (event, newValue)=>onChange?.(event, newValue);
    const handleStep = (event, newStep)=>{
        const newValue = Number(value) + newStep;
        changeValue(event, rawOutput ? newValue.toString() : newValue);
        onBlur?.(event);
    };
    const handleInputChange = (event)=>{
        if (!isNaN(Number(event.target.value)) && !disableDirectInput) {
            const newValue = rawOutput ? event.target.value : Math.floor(Number(event.target.value));
            changeValue(event, newValue);
        }
    };
    const renderPanelControls = ()=>{
        const isNotNumber = isNaN(value);
        const disabledMax = isNotNumber || isOutOfMaxRange(max, Number(value) + step);
        const disabledMin = isNotNumber || isOutOfMinRange(min, Number(value) - step);
        return controlsPanelStepUpIcon && controlsPanelStepDownIcon ? /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(InputControl, {
            className: "Input-increment",
            disabled: disabled || disabledMax,
            icon: controlsPanelStepUpIcon,
            onClick: (event)=>handleStep(event, step)
        }), /*#__PURE__*/ React.createElement(InputControl, {
            className: "Input-decrement",
            disabled: disabled || disabledMin,
            icon: controlsPanelStepDownIcon,
            onClick: (event)=>handleStep(event, -step)
        })) : null;
    };
    const textfieldProps = {
        id,
        labelText,
        labelNote,
        labelClassName,
        labelPosition,
        floatingLabelText,
        floatingLabelClassName,
        controlClassName,
        controlInputClassName,
        validationText,
        validationClassName,
        hideValidationText,
        disabled,
        placeholder,
        value: displayValue ? displayValue : value,
        panelControls: renderPanelControls(),
        onChange: handleInputChange,
        onBlur: handleOnBlur,
        onFocus,
        onClick,
        readOnly: disableDirectInput,
        outline,
        tooltip,
        required,
        onKeyUp,
        validationDisplayType,
        sizeSmall,
        simple
    };
    return /*#__PURE__*/ React.createElement("div", {
        className: cx('Spinner', className)
    }, /*#__PURE__*/ React.createElement(TextField, textfieldProps));
};
Spinner.propTypes = {
    /**
	 * Optionally provide own input element as component children.
	 */ children: PropTypes.node,
    /**
	 * Class of Spinner container.
	 */ className: PropTypes.string,
    /**
	 * Set class for control. Control is component in between actual input/textarea and TextField.
	 */ controlClassName: PropTypes.string,
    /**
	 * Set classname for input, when input is used. This class will also be used for custom children.
	 */ controlInputClassName: PropTypes.string,
    /**
	 * Name of the icon represents control to decrease value.
	 */ controlsPanelStepDownIcon: PropTypes.node,
    /**
	 * Name of the icon represents control to increase value.
	 */ controlsPanelStepUpIcon: PropTypes.node,
    /**
	 * Flag, indicating whether value change is possible only using control arrow
	 */ disableDirectInput: PropTypes.bool,
    /**
	 * Whether control should be disabled. Disabled elements are not editable, focusable, and are not sent with form when submitting.
	 */ disabled: PropTypes.bool,
    /**
	 * If present, this is used for display in the input field of the spinner
	 */ displayValue: PropTypes.any,
    /**
	 * If truthy, the current min max range will be enforced by not allowing user to exceed it.
	 * For example if user writes a number 10 into a Spinner when max is set to 5 then final value will be forced to 5
	 */ enforceRange: PropTypes.bool,
    /**
	 * Set class for floating label.
	 */ floatingLabelClassName: PropTypes.string,
    /**
	 * Set floating label. Floating label is displayed in place of control text. When control gets focused or has value, the label "floats" away, but is still visible.
	 */ floatingLabelText: PropTypes.node,
    /**
	 * Whether to render validation component.
	 */ hideValidationText: PropTypes.bool,
    /**
	 * Id of the form element. If not specified, one will be generated automatically to connect input with label using the `for` attribute.
	 */ id: PropTypes.string,
    /**
	 * Set class of static label.
	 */ labelClassName: PropTypes.string,
    /**
	 * Static label note text. Label note is directly attached to label text, and displayed bellow with more subtle style.
	 */ labelNote: PropTypes.node,
    /**
	 * Position of label (`left`, `right`). By default `left` is used, which leads to displaying label on top of the input. When right is used, label will be displayed right of the input.
	 */ labelPosition: PropTypes.oneOf(FormGroup.labelPositions.values()),
    /**
	 * Static label text.
	 */ labelText: PropTypes.node,
    /**
	 * When value = max the increase control is disabled.
	 */ max: PropTypes.number,
    /**
	 * When value = min the decrease control is disabled.
	 */ min: PropTypes.number,
    /**
	 * Control focus lost callback
	 *
	 * @param {object} event event targeting the element.
	 * @return {undefined} Nothing
	 */ onBlur: PropTypes.func,
    /**
	 * Control value changed callback
	 *
	 * @param {object} event Event targeting the element.
	 * @param {object} value New control value
	 * @return {undefined} Nothing
	 */ onChange: PropTypes.func,
    /**
	 * Control clicked callback
	 *
	 * @param {object} event event targeting the element.
	 * @return {undefined} Nothing
	 */ onClick: PropTypes.func,
    /**
	 * Control focused callback
	 *
	 * @param {object} event event targeting the element.
	 * @return {undefined} Nothing
	 */ onFocus: PropTypes.func,
    /**
	 * Control keyUp keyboard callback
	 *
	 * @param {object} event Event targeting the element.
	 * @return {undefined} Nothing
	 */ onKeyUp: PropTypes.func,
    /**
	 * Flag indicating whether to user outlined input or not
	 */ outline: PropTypes.bool,
    /**
	 * Custom control panel for inner TextField Component
	 */ panelControls: PropTypes.node,
    /**
	 * Native placeholder. The prop will be passed to input/textarea, but also to custom children - meaning we can specify placeholder directly on TextField component and not inside children.
	 */ placeholder: PropTypes.string,
    /**
	 * Raw output will return string instead of parsed number. It is same behavior as native html input.
	 */ rawOutput: PropTypes.bool,
    /**
	 * Show whether Component is required
	 */ required: PropTypes.bool,
    /**
	 * If true, Spinner will be rendered using SimpleInput
	 */ simple: PropTypes.bool,
    /**
	 * If true, Input will be rendered with sizeSmall modifier
	 */ sizeSmall: PropTypes.bool,
    /**
	 * How much is increased/decreased value for one click.
	 */ step: PropTypes.number,
    /**
	 * Tooltip children, either string or custom element
	 */ tooltip: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ]),
    /**
	 * Set class for validation component.
	 */ validationClassName: PropTypes.string,
    /**
	 * Where to display validation
	 */ validationDisplayType: validationDisplayTypePropType,
    /**
	 * Text that will be shown when withValidity decorator decides to show validation text.
	 */ validationText: PropTypes.node,
    /**
	 * Control value
	 *
	 * @param {object} event event targeting the element.
	 * @return {undefined} Nothing
	 */ value: PropTypes.any
};
export { Spinner };
export default Spinner;
